import React, { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, Edit, Package, Scale, RefreshCw, AlertTriangle, CheckCircle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { toast } from "sonner";
import api from "../utils/api";
import { formatMXN } from "../utils/format";

function ReceivingForm({ receiving, suppliers, products, onClose, onSave }) {
  const [form, setForm] = useState({
    supplier_id: "", supplier_name: "", product_id: "", product_name: "",
    purchased_qty: "", purchased_unit: "kg", purchase_unit_cost: "",
    real_qty: "", supplier_lot: "", notes: "", status: "pesado",
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (receiving) setForm({
      supplier_id: receiving.supplier_id || "", supplier_name: receiving.supplier_name || "",
      product_id: receiving.product_id || "", product_name: receiving.product_name || "",
      purchased_qty: receiving.purchased_qty || "", purchased_unit: receiving.purchased_unit || "kg",
      purchase_unit_cost: receiving.purchase_unit_cost || "", real_qty: receiving.real_qty || "",
      supplier_lot: receiving.supplier_lot || "", notes: receiving.notes || "",
      status: receiving.status || "pesado",
    });
  }, [receiving]);

  const purchasedQty = parseFloat(form.purchased_qty) || 0;
  const realQty = parseFloat(form.real_qty) || 0;
  const unitCost = parseFloat(form.purchase_unit_cost) || 0;
  const totalCost = purchasedQty * unitCost;
  const waste = realQty > 0 ? purchasedQty - realQty : 0;
  const wastePct = purchasedQty > 0 && realQty > 0 ? ((waste / purchasedQty) * 100).toFixed(1) : 0;
  const realCostPerUnit = realQty > 0 ? (totalCost / realQty).toFixed(4) : 0;

  const rawProducts = products.filter(p => p.category === "materia_prima" || p.category === "insumo");

  const handleSave = async () => {
    if (!form.product_id) { toast.error("Selecciona un producto"); return; }
    if (purchasedQty <= 0) { toast.error("Indica la cantidad comprada"); return; }
    setSaving(true);
    try {
      const payload = { ...form, purchased_qty: purchasedQty, real_qty: realQty, purchase_unit_cost: unitCost };
      if (receiving) { await api.put(`/receivings/${receiving.id}`, payload); toast.success("Recepcion actualizada"); }
      else { await api.post("/receivings", payload); toast.success("Recepcion registrada"); }
      onSave();
    } catch (err) { const d = err.response?.data?.detail; toast.error(typeof d === "string" ? d : "Error"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="receiving-form">
        <DialogHeader><DialogTitle>{receiving ? "Editar Recepcion" : "Nueva Recepcion de Materia Prima"}</DialogTitle></DialogHeader>
        <div className="space-y-3 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Proveedor</label>
              <select value={form.supplier_id} onChange={e => {
                const s = suppliers.find(s => s.id === e.target.value);
                setForm(f => ({ ...f, supplier_id: e.target.value, supplier_name: s?.name || "" }));
              }} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="rec-supplier">
                <option value="">Seleccionar...</option>
                {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Materia Prima *</label>
              <select value={form.product_id} onChange={e => {
                const p = products.find(p => p.id === e.target.value);
                setForm(f => ({ ...f, product_id: e.target.value, product_name: p?.name || "", purchased_unit: p?.unit || "kg" }));
              }} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="rec-product">
                <option value="">Seleccionar...</option>
                {rawProducts.map(p => <option key={p.id} value={p.id}>{p.name} ({p.unit})</option>)}
              </select>
            </div>
          </div>
          <div className="bg-amber-50 border border-amber-100 rounded-lg p-3">
            <div className="text-xs font-bold text-amber-800 mb-2">Pesos y Costos</div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-[10px] font-medium text-amber-700 mb-1">Cantidad comprada ({form.purchased_unit})</label>
                <input type="number" value={form.purchased_qty} onChange={e => setForm(f => ({ ...f, purchased_qty: e.target.value }))}
                  className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm" placeholder="110" min="0" step="0.1" data-testid="rec-purchased-qty" />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-amber-700 mb-1">Costo por {form.purchased_unit} ($)</label>
                <input type="number" value={form.purchase_unit_cost} onChange={e => setForm(f => ({ ...f, purchase_unit_cost: e.target.value }))}
                  className="w-full border border-amber-200 rounded-lg px-3 py-2 text-sm" placeholder="44.50" min="0" step="0.01" data-testid="rec-unit-cost" />
              </div>
              <div>
                <label className="block text-[10px] font-medium text-green-700 mb-1">Peso real post-descongelado ({form.purchased_unit})</label>
                <input type="number" value={form.real_qty} onChange={e => setForm(f => ({ ...f, real_qty: e.target.value }))}
                  className="w-full border border-green-200 rounded-lg px-3 py-2 text-sm bg-green-50 font-bold" placeholder="98.1" min="0" step="0.1" data-testid="rec-real-qty" />
              </div>
            </div>
          </div>
          {realQty > 0 && purchasedQty > 0 && (
            <div className="grid grid-cols-3 gap-2">
              <div className="bg-slate-50 rounded-lg p-2 text-center">
                <div className="text-[10px] text-slate-500">Merma</div>
                <div className="text-sm font-bold text-red-600">{waste.toFixed(1)} {form.purchased_unit} ({wastePct}%)</div>
              </div>
              <div className="bg-slate-50 rounded-lg p-2 text-center">
                <div className="text-[10px] text-slate-500">Costo Total</div>
                <div className="text-sm font-bold text-slate-800">{formatMXN(totalCost)}</div>
              </div>
              <div className="bg-emerald-50 rounded-lg p-2 text-center">
                <div className="text-[10px] text-emerald-600">Costo REAL por {form.purchased_unit}</div>
                <div className="text-sm font-bold text-emerald-700">{formatMXN(Number(realCostPerUnit))}</div>
              </div>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Lote proveedor</label>
              <input value={form.supplier_lot} onChange={e => setForm(f => ({ ...f, supplier_lot: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Ej: LOT-2026-001" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-600 mb-1">Estado</label>
              <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}
                className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" data-testid="rec-status">
                <option value="pendiente">Pendiente de recoger</option>
                <option value="descongelando">Descongelando</option>
                <option value="pesado">Pesado (listo)</option>
                <option value="liberado">Liberado a produccion</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-600 mb-1">Notas</label>
            <input value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm" placeholder="Observaciones..." />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">Cancelar</button>
            <button onClick={handleSave} disabled={saving} data-testid="save-receiving-btn"
              className="px-6 py-2 bg-brand-600 hover:bg-brand-700 text-white rounded-lg text-sm font-medium disabled:opacity-60">
              {saving ? "Guardando..." : "Registrar Recepcion"}
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

const STATUS_COLORS = {
  pendiente: "bg-slate-100 text-slate-600",
  descongelando: "bg-blue-100 text-blue-700",
  pesado: "bg-green-100 text-green-700",
  liberado: "bg-emerald-100 text-emerald-700",
};
const STATUS_LABELS = { pendiente: "Pendiente", descongelando: "Descongelando", pesado: "Pesado", liberado: "Liberado" };

export default function Recepciones() {
  const [receivings, setReceivings] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [recRes, supRes, prodRes] = await Promise.all([
        api.get("/receivings"), api.get("/suppliers"), api.get("/products"),
      ]);
      setReceivings(recRes.data); setSuppliers(supRes.data); setProducts(prodRes.data);
    } catch { toast.error("Error al cargar"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const deleteReceiving = async (id) => {
    if (!window.confirm("Eliminar recepcion?")) return;
    await api.delete(`/receivings/${id}`); toast.success("Eliminada"); load();
  };

  return (
    <div className="space-y-5" data-testid="recepciones-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">Recepciones de Materia Prima</h1>
          <p className="text-slate-500 text-sm mt-0.5">Control de peso real post-descongelado y costo real</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} className="flex items-center gap-1 px-3 py-2 border border-slate-200 rounded-lg text-sm text-slate-600 hover:bg-slate-50">
            <RefreshCw size={14} /> Actualizar
          </button>
          <button onClick={() => { setEditing(null); setShowForm(true); }} data-testid="new-receiving-btn"
            className="flex items-center gap-2 bg-brand-600 hover:bg-brand-700 text-white px-4 py-2 rounded-lg text-sm font-medium">
            <Plus size={16} /> Nueva Recepcion
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="receivings-table">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              {["Folio", "Proveedor", "Producto", "Comprado", "Peso Real", "Merma", "Costo/U Compra", "Costo REAL/U", "Estado", ""].map(h => (
                <th key={h} className="text-left px-3 py-2.5 text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={10} className="text-center py-12 text-slate-400">Cargando...</td></tr>
            ) : receivings.length === 0 ? (
              <tr><td colSpan={10} className="text-center py-12 text-slate-400">
                <Scale size={32} className="mx-auto text-slate-300 mb-2" />
                <p>Sin recepciones registradas</p>
              </td></tr>
            ) : receivings.map(r => (
              <tr key={r.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`rec-row-${r.id}`}>
                <td className="px-3 py-2.5 font-medium text-brand-600">{r.folio}</td>
                <td className="px-3 py-2.5 text-slate-700">{r.supplier_name || "-"}</td>
                <td className="px-3 py-2.5 text-slate-700">{r.product_name}</td>
                <td className="px-3 py-2.5 font-medium">{r.purchased_qty} {r.purchased_unit}</td>
                <td className="px-3 py-2.5 font-bold text-emerald-700">{r.real_qty > 0 ? `${r.real_qty} ${r.purchased_unit}` : "-"}</td>
                <td className="px-3 py-2.5">
                  {r.waste_qty > 0 ? (
                    <span className="text-red-600 text-xs font-medium">{r.waste_qty.toFixed(1)} ({r.waste_pct}%)</span>
                  ) : "-"}
                </td>
                <td className="px-3 py-2.5">{formatMXN(r.purchase_unit_cost)}</td>
                <td className="px-3 py-2.5 font-bold text-emerald-700">{r.real_unit_cost > 0 ? formatMXN(r.real_unit_cost) : "-"}</td>
                <td className="px-3 py-2.5">
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_COLORS[r.status] || "bg-slate-100"}`}>
                    {STATUS_LABELS[r.status] || r.status}
                  </span>
                </td>
                <td className="px-3 py-2.5">
                  <div className="flex gap-1">
                    <button onClick={() => { setEditing(r); setShowForm(true); }} className="p-1 hover:bg-slate-100 rounded"><Edit size={14} className="text-slate-500" /></button>
                    <button onClick={() => deleteReceiving(r.id)} className="p-1 hover:bg-red-50 rounded"><Trash2 size={14} className="text-red-400" /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showForm && <ReceivingForm receiving={editing} suppliers={suppliers} products={products}
        onClose={() => setShowForm(false)} onSave={() => { setShowForm(false); load(); }} />}
    </div>
  );
}
